<template>
  <el-main class="iDalgo-Users">

    <!-- Template title -->
    <el-row :gutter="20">
      <el-col :span="21">
        <h2>{{ $t('iDalgoCRM.title') }}</h2>
      </el-col>
      <el-col :span="3" class="flex-end">
        <i-dalgo-button @click="goToNewUserPage" :border="true">+ {{ $t('actions.newButton') }}</i-dalgo-button>
      </el-col>
    </el-row>
    <div class="mrg-20"></div>

    <!-- users -->
    <div v-loading="loading">
      <el-table
        v-loading="loading"
        :data="formattedUsers"
        :default-sort = "{prop: 'createdAt', order: 'descending'}"
        style="width: 100%">
        <template>
          <el-table-column prop="createdAt"       label="Créé le"               sortable :formatter="getFormattedCreatedAt"></el-table-column>
          <el-table-column prop="email"           label="Adresse mail"          sortable></el-table-column>
          <el-table-column prop="customer"        label="Client"                sortable></el-table-column>
          <el-table-column prop="surveyCount"     label="Nb sondages"           sortable></el-table-column>
          <el-table-column prop="lastSurveyDate"  label="Date dernier sondage"  sortable></el-table-column>
          <el-table-column label="Opérations">
            <template
              slot-scope="scope">
              <div class="operations" style="display: flex;justify-content: space-between;">
                <div class="operations-item" @click="connectAs(scope.row.id)">
                  <i class="el-icon-view" />
                </div>
                <div class="operations-item" @click="goToEditPage(scope.row.id)">
                  <svgicon name="pen"></svgicon>
                </div>
                <div class="operations-item" @click="deleteUser(scope.row.id)">
                  <svgicon name="bin"></svgicon>
                </div>
              </div>
            </template>
          </el-table-column>
        </template>
      </el-table>
    </div>
    <div class="mrg-20"></div>

    <!-- no template -->
    <el-row v-if="users.length === 0 && !$route.query.search">
      <el-col :span="24" style="justify-content: center;">
        <div style="text-align: center;">
          <b>{{ $t('iDalgoCRM.noDataInfo') }}</b><br>
          <h2>{{ $t('iDalgoCRM.noDataCTA') }}</h2><br>
          <i-dalgo-button @click="goToNewUserPage" :border="true">+ {{ $t('actions.newButton') }}</i-dalgo-button>
        </div>
      </el-col>
    </el-row>
    <el-row v-else-if="users.length === 0">
      <el-col :span="24" style="justify-content: center;">
        <div style="text-align: center;">
          <b>{{ $t('iDalgoCRM.noDataFound') }}</b><br>
        </div>
      </el-col>
    </el-row>
  </el-main>

</template>

<script>
import { mapGetters } from 'vuex'
import iDalgoButton from '@/components/iDalgo-Button'
import '@/components/icons'
import { productData } from '@/api'
import moment from 'moment'

export default {
  name: 'iDalgo-Users',
  components: {
    iDalgoButton
  },
  computed: {
    ...mapGetters({
      surveys: 'productData/surveys',
      users: 'users/users',
      customers: 'customers/customers',
      loading: 'users/isLoading'
    }),
    userSurveys () {
      return userId => {
        return this.surveys
          .filter(survey => survey.userId === userId)
          .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) // sorted by descending order : index 0 = latest
      }
    },
    surveyCount () {
      return userId => {
        return this.userSurveys(userId).length
      }
    },
    lastSurveyDate () {
      return userId => {
        const lastSurvey = this.userSurveys(userId)[0]
        return lastSurvey
          ? moment(lastSurvey.createdAt).format('DD/MM/Y')
          : 'N/A'
      }
    },
    customer () {
      return customerId => {
        const customer = this.customers.find(customer => customer.id === customerId)
        return customer
          ? customer.txt_name
          : 'Inconnu'
      }
    },
    formattedUsers () {
      return this.users.map(user => Object.assign({
        surveyCount: this.surveyCount(user.id),
        lastSurveyDate: this.lastSurveyDate(user.id),
        customer: this.customer(user.customerId)
      }, user))
    }
  },
  data () {
    return {}
  },
  methods: {
    getFormattedCreatedAt (row, column, cellValue, index) {
      return moment(cellValue).format('DD/MM/Y')
    },
    goToNewUserPage () {
      this.$router.push({ name: 'newUser' })
    },
    goToEditPage (userId) {
      this.$router.push({ name: 'editUser', params: { id: userId } })
    },
    deleteUser (userId) {
      let message = this.$t('iDalgoDialogs.deleteUser.notification')
      this.$confirm(this.$t('iDalgoDialogs.deleteUser.message'), this.$t('iDalgoDialogs.deleteUser.title'), {
        confirmButtonText: this.$t('actions.delete'),
        cancelButtonText: this.$t('actions.cancelButton'),
        type: 'warning'
      }).then(() => {
        this.$store.dispatch('users/deleteUser', userId).then(() => {
          this.$notify({ message })
        })
      }).catch(() => {})
    },
    connectAs (userId) {
      this.$store.dispatch('auth/connectAs', userId)
    }
  },
  async mounted () {
    // get the list of users of the current page
    await this.$store.dispatch('users/getUsers', this.$route.query)
    const surveys = (await productData.fetchSurveys()).data
    this.$store.commit('productData/SURVEYS_UPDATED', surveys)
  }
}
</script>

<style lang="scss" scoped>
h2 {
  font-size: 40px;
  font-family: 'robotobold';
  margin: 0;
  color: $color1;
}

.users-action {
  color: $logo-gray;
  font-family: 'robotobold';
  font-size: 14px;
}
</style>

<style lang="scss">
.iDalgo-Users {
  .iDalgo-Button {
    .el-button {
      font-size: 18px;
      padding: $default_padding $default_padding * 2;
    }
  }
  .col-search {
    padding: 5px;
    input {
      padding-left: 51px;
      border: none;
      height: 38px;
      width: 413px;
      border-radius: 8px;
      background-color: #FFFFFF;
      box-shadow: inset 0 1px 3px 0 rgba( 0, 0 ,0 ,0.08 );
    }
  }

  .operations {
    &-item {
      cursor: pointer;
      background: #F5F5F5;
      width: 30px;
      height: 30px;
      border-radius: 4px;
      display: flex;
      justify-content: center;
      align-items: center;

      &:hover {
        background: #EEE;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/iDalgo-Users.vue